import { useStore } from '@nanostores/react';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { useBuilderBalance } from '@/features/billing-usage';
import { type CreditPurchaseGating, computeCreditPurchaseGating } from '../utils/credit-purchase-gating';
import { deriveIsTrial } from '../utils/trial-state';
import { useBillingEditGuard } from './use-billing-edit-guard';

export type { CreditPurchaseGating } from '../utils/credit-purchase-gating';

/**
 * The disabled state and tooltip copy for the credit-purchase CTAs. Gathers the
 * reactive account/billing/permission inputs and delegates the branching to the pure
 * {@link computeCreditPurchaseGating}.
 */
export function useCreditPurchaseGating(): CreditPurchaseGating {
  const { account } = useAuth();
  const editGuard = useBillingEditGuard();
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const isOrdwayVendor = useStore(billing.isOrdwayVendor);
  const isStripeCustomer = useStore(billing.isStripeCustomer);
  const isBillingTrial = useStore(billing.isTrial);
  const isPendingCheckout = useStore(billing.isPendingCheckout);
  const { balanceCents, isLoading: isBalanceLoading } = useBuilderBalance();

  const isTrial = deriveIsTrial({
    ordwayId: account.ordway_id,
    isBillingTrial,
    isPendingCheckout,
    messagingEnabled: account.planFeatures.messaging.enabled === true,
    messagingExpiresAt: account.planFeatures.messaging.expiresAt,
  });

  return computeCreditPurchaseGating({
    state: { accountState: account.account_state, isPaused: account.is_paused, isTrial },
    editDisabled: editGuard.isDisabled,
    editDisabledMessage: editGuard.disabledMessage,
    isOrdwayCustomer,
    isOrdwayVendor,
    isStripeCustomer,
    balanceCents,
    isBalanceLoading,
  });
}
